import { ENEMY_XP_REWARD } from '../data/skills';
import type { SeededRng } from './rng';
import {
  acceptEnhancementOffer,
  buildEnhancementOffer,
  eligibleEnhancementsForOffer,
} from './enhancements';
import {
  skillPassesEquipmentGate,
  weaponConfigFromSnapshot,
} from './equipmentEligibility';
import type { EquipmentSnapshot } from './equipmentCombat';
import {
  assignSkillToDie,
  buildSkillOffer,
  findDieWithSkill,
  getSkillDefinition,
  listPrimarySkillPool,
  replaceSkillOnDie,
  upgradeSkillOnDie,
} from './skills';
import {
  SKILL_OFFER_REROLLS_PER_DUNGEON,
  TEMP_XP_TO_NEXT_LEVEL,
  type DiceTrayState,
  type SkillDefinition,
  type SkillOffer,
  type TemporaryProgress,
} from './types';

export function createTemporaryProgress(): TemporaryProgress {
  return {
    level: 1,
    xp: 0,
    pendingLevelUps: 0,
    skillOfferRerollsRemaining: SKILL_OFFER_REROLLS_PER_DUNGEON,
    awaitingFirstPrimary: true,
    currentOffer: null,
    offerKind: null,
    selectedEnhancementIds: [],
  };
}

export function xpToReachNextLevel(level: number): number {
  if (level < 1) {
    return TEMP_XP_TO_NEXT_LEVEL[1]!;
  }
  if (level >= TEMP_XP_TO_NEXT_LEVEL.length - 1) {
    return TEMP_XP_TO_NEXT_LEVEL[TEMP_XP_TO_NEXT_LEVEL.length - 1]!;
  }
  return TEMP_XP_TO_NEXT_LEVEL[level]!;
}

export function xpForEnemyTemplate(templateId: string): number {
  return ENEMY_XP_REWARD[templateId] ?? 10;
}

/**
 * Adds temporary dungeon XP and queues level-ups. Does not open offers yet.
 */
export function gainTemporaryXp(
  progress: TemporaryProgress,
  amount: number,
): TemporaryProgress {
  if (amount <= 0) {
    return progress;
  }
  let xp = progress.xp + amount;
  let level = progress.level;
  let pending = progress.pendingLevelUps;
  let guard = 0;
  while (xp >= xpToReachNextLevel(level) && guard < 20) {
    xp -= xpToReachNextLevel(level);
    level += 1;
    pending += 1;
    guard += 1;
  }
  return {
    ...progress,
    level,
    xp,
    pendingLevelUps: pending,
  };
}

function shuffleCopy<T>(items: readonly T[], rng: SeededRng): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = rng.nextInt(0, i);
    const tmp = copy[i]!;
    copy[i] = copy[j]!;
    copy[j] = tmp;
  }
  return copy;
}

export interface OfferRollContext {
  unlockedActiveSkillIds?: readonly string[];
  /** Dungeon-entry equipment snapshot for weapon gates. */
  equipmentSnapshot?: EquipmentSnapshot | null;
}

/**
 * Eligible skills for offers:
 * - pool = unlocked permanent actives, or provisional M3 pool if none unlocked
 * - locked permanent actives never appear
 * - first primary: any skill from that pool (equipment gates apply)
 * - later: upgrades of owned + unowned from pool; maxed owned omitted
 * - equipmentGate uses dungeon snapshot (not live hub inventory)
 */
export function eligibleSkillsForOffer(
  progress: TemporaryProgress,
  tray: DiceTrayState,
  unlockedActiveSkillIds: readonly string[] = [],
  equipmentSnapshot: EquipmentSnapshot | null = null,
): SkillDefinition[] {
  const pool = listPrimarySkillPool(unlockedActiveSkillIds);
  const weaponConfig = weaponConfigFromSnapshot(equipmentSnapshot);
  const gated = pool.filter((skill) =>
    skillPassesEquipmentGate(skill, weaponConfig),
  );
  if (progress.awaitingFirstPrimary) {
    return gated.length > 0 ? [...gated] : [...pool];
  }

  const result: SkillDefinition[] = [];
  for (const skill of gated) {
    const owned = findDieWithSkill(tray, skill.id);
    if (owned) {
      if (owned.primarySkillLevel < skill.maxLevel) {
        result.push(skill);
      }
      continue;
    }
    result.push(skill);
  }

  if (result.length === 0) {
    return gated.length > 0 ? [...gated] : [...pool];
  }
  return result;
}

export function rollSkillOffers(
  progress: TemporaryProgress,
  tray: DiceTrayState,
  rng: SeededRng,
  count = 3,
  unlockedActiveSkillIds: readonly string[] = [],
  equipmentSnapshot: EquipmentSnapshot | null = null,
): TemporaryProgress {
  const skillEligible = eligibleSkillsForOffer(
    progress,
    tray,
    unlockedActiveSkillIds,
    equipmentSnapshot,
  );
  const enhEligible = eligibleEnhancementsForOffer(
    progress,
    tray,
    unlockedActiveSkillIds,
  );

  // Mix skills + enhancements into one pool of offer cards; preserve 3-card UI.
  type Card =
    | { kind: 'skill'; skill: SkillDefinition }
    | { kind: 'enhancement'; enhancementId: string };
  const cards: Card[] = [
    ...skillEligible.map((skill) => ({ kind: 'skill' as const, skill })),
    ...enhEligible.map((e) => ({
      kind: 'enhancement' as const,
      enhancementId: e.id,
    })),
  ];
  const shuffled = shuffleCopy(cards, rng);
  const picked = shuffled.slice(0, Math.min(count, shuffled.length));
  while (picked.length < count && cards.length > 0) {
    const extra = shuffleCopy(cards, rng).find(
      (c) =>
        !picked.some((p) =>
          p.kind === 'skill' && c.kind === 'skill'
            ? p.skill.id === c.skill.id
            : p.kind === 'enhancement' &&
              c.kind === 'enhancement' &&
              p.enhancementId === c.enhancementId,
        ),
    );
    if (!extra) break;
    picked.push(extra);
  }

  const offers: SkillOffer[] = picked.map((card) => {
    if (card.kind === 'enhancement') {
      const def = enhEligible.find((e) => e.id === card.enhancementId)!;
      return buildEnhancementOffer(def);
    }
    return buildSkillOffer(card.skill, tray);
  });

  return {
    ...progress,
    currentOffer: offers,
    offerKind: progress.awaitingFirstPrimary ? 'first_primary' : 'standard',
  };
}

export function rerollSkillOffers(
  progress: TemporaryProgress,
  tray: DiceTrayState,
  rng: SeededRng,
  unlockedActiveSkillIds: readonly string[] = [],
  equipmentSnapshot: EquipmentSnapshot | null = null,
): TemporaryProgress {
  if (progress.skillOfferRerollsRemaining <= 0) {
    throw new Error('No skill-offer rerolls remaining this dungeon');
  }
  if (!progress.currentOffer) {
    throw new Error('No active skill offer to reroll');
  }
  const next = rollSkillOffers(
    progress,
    tray,
    rng,
    3,
    unlockedActiveSkillIds,
    equipmentSnapshot,
  );
  return {
    ...next,
    skillOfferRerollsRemaining: progress.skillOfferRerollsRemaining - 1,
  };
}

export interface AcceptSkillResult {
  progress: TemporaryProgress;
  tray: DiceTrayState;
  /** When set, UI should ask the player which die to replace. */
  needsDieSelection: boolean;
  skillId: string;
}

/**
 * Accepts one offer card by offerId (skill id or enhancement id).
 * Upgrades apply immediately on the owned die.
 * New skills always require an explicit target die id (assign or replace).
 * Enhancements persist on TemporaryProgress (no die).
 */
export function acceptSkillOffer(
  progress: TemporaryProgress,
  tray: DiceTrayState,
  offerId: string,
  targetDieId?: number,
): AcceptSkillResult {
  if (!progress.currentOffer) {
    throw new Error('No active skill offer');
  }
  if (progress.pendingLevelUps <= 0) {
    throw new Error('No pending level-ups');
  }
  const offer = progress.currentOffer.find(
    (o) => o.offerId === offerId || o.skillId === offerId,
  );
  if (!offer) {
    throw new Error(`Offer ${offerId} is not in the current offer`);
  }

  if (offer.kind === 'enhancement' && offer.enhancementId) {
    const nextProgress = acceptEnhancementOffer(progress, offer.enhancementId);
    return {
      progress: nextProgress,
      tray,
      needsDieSelection: false,
      skillId: offer.enhancementId,
    };
  }

  const skillId = offer.skillId;
  getSkillDefinition(skillId);

  let nextTray = tray;

  if (offer.isUpgrade) {
    nextTray = upgradeSkillOnDie(tray, skillId);
  } else if (targetDieId === undefined) {
    return {
      progress,
      tray,
      needsDieSelection: true,
      skillId,
    };
  } else {
    const target = tray.dice.find((die) => die.id === targetDieId);
    if (!target?.unlocked) {
      throw new Error(`Cannot place skill on die ${targetDieId}`);
    }
    nextTray =
      target.primarySkillId === null
        ? assignSkillToDie(tray, targetDieId, skillId)
        : replaceSkillOnDie(tray, targetDieId, skillId);
  }

  const pending = progress.pendingLevelUps - 1;
  const nextProgress: TemporaryProgress = {
    ...progress,
    pendingLevelUps: pending,
    awaitingFirstPrimary: false,
    currentOffer: null,
    offerKind: null,
  };

  return {
    progress: nextProgress,
    tray: nextTray,
    needsDieSelection: false,
    skillId,
  };
}

export function describeOffer(offer: SkillOffer): string {
  if (offer.kind === 'enhancement') {
    return `${offer.displayName ?? offer.offerId} (enhancement)`;
  }
  const skill = getSkillDefinition(offer.skillId);
  if (offer.isUpgrade) {
    return `${skill.name} Lv${offer.currentLevel}→${offer.nextLevel}`;
  }
  return `${skill.name} (new)`;
}
